import {
  BiCardDataState,
  BiDashboardFilters,
  BiDateFilterUse,
} from '@euler/model/bi/dashboard';
import { BiDraftResult } from '@euler/model/bi/draft';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { biErrorMessage } from '../../functions/biErrors';

export type BiCardDataView =
  | { kind: 'loading' }
  | { kind: 'ready'; result: BiDraftResult; dateFilter: BiDateFilterUse }
  /** The viewer's role cannot read a field the card uses: title only. */
  | { kind: 'restricted' }
  | { kind: 'failed'; message: string };

/**
 * One card's data, loaded on its own so a slow or failing card never holds
 * up the others. Loads again when the card or the filters change, and not
 * before `enabled` (a dashboard card off screen waits until it is seen).
 */
export function useBiCardData(
  cardId: string,
  /** The card's `updatedAt`: an edit reloads it. */
  version: string,
  /** The request's filters; undefined applies the dashboard's saved ones. */
  filters: BiDashboardFilters | undefined,
  /** The saved filters, so a saved change reloads cards that follow them. */
  savedFilters: BiDashboardFilters,
  {
    enabled = true,
    refreshTick = 0,
  }: {
    /** False holds the read back, e.g. until the card is on screen. */
    enabled?: boolean;
    /** Bumped to read every card of the dashboard again. */
    refreshTick?: number;
  } = {},
) {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiCardDataView>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const filtersKey = JSON.stringify(filters ?? null);
  const savedKey = JSON.stringify(savedFilters);

  useEffect(() => {
    // No card yet (its dashboard still loading): nothing to read.
    if (!cardId || !enabled) return;
    let active = true;
    setState({ kind: 'loading' });
    const request: BiDashboardFilters | undefined = JSON.parse(filtersKey);
    biService
      .cardData(cardId, request ? { filters: request } : {})
      .then(data => {
        if (!active) return;
        setState(
          data.state === BiCardDataState.Restricted
            ? { kind: 'restricted' }
            : {
                kind: 'ready',
                result: data.result,
                dateFilter: data.dateFilter,
              },
        );
      })
      .catch(error => {
        console.error('BI card data failed', cardId, error);
        if (active)
          setState({
            kind: 'failed',
            message: biErrorMessage(error, '这张图表没有加载出来。'),
          });
      });
    return () => {
      active = false;
    };
  }, [
    biService,
    cardId,
    version,
    filtersKey,
    savedKey,
    reload,
    enabled,
    refreshTick,
  ]);

  const retry = useCallback(() => setReload(value => value + 1), []);
  return { state, retry };
}
